import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import { useLocation } from 'react-router-dom';
import { Button, BUTTON_TYPES, Card } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { MixpanelService } from 'helpers/Mixpanel';
import { signIn } from 'actions';
import type { AppDispatch } from 'store';
import { ReactComponent as GoogleLogo } from 'dist/icons/google.svg';
import { ReactComponent as Logo } from 'dist/icons/logo.svg';
import { useLoginState } from './utils';
import { LOGIN_TYPES, type LoginType } from './constants';
import './style.scss';

interface LoginProps {
  clearError?: () => void;
}

interface LocationErrorState {
  error?: LoginType | string;
}

export const Login: React.FC<LoginProps> = ({ clearError }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation<LocationErrorState>();
  const [messageType, setMessageType] = useState(location?.state?.error ?? LOGIN_TYPES.WELCOME);
  const [title, extra] = useLoginState(messageType, t);

  const login = useGoogleLogin({
    onSuccess: ({ code }) => dispatch(signIn(code, setMessageType)),
    // eslint-disable-next-line no-console -- preserved from original login error logging
    onNonOAuthError: error => console.log('error sign in', error),
    scope: 'email profile openid',
    overrideScope: true,
    flow: 'auth-code',
  });

  const onGoogleLogin = () => {
    localStorage.removeItem('access_token');
    if (clearError) clearError();
    login();
  };

  useEffect(() => {
    sessionStorage.clear();
    const mixpanel = new MixpanelService();
    mixpanel.track('View Page.Login');
  }, []);

  return (
    <div className="login-page">
      <Card className="login-card">
        <Logo className="login-card__app-name" />
        <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD} className="login-card__title">
          {title}
        </Typography.Text>
        <Typography.Text className="login-card__subtitle">{extra}</Typography.Text>
        <Button className="original-svg" type={BUTTON_TYPES.PRIMARY} icon={<GoogleLogo />} onClick={onGoogleLogin}>
          {t('translation:login.button_google_login')}
        </Button>
      </Card>
    </div>
  );
};
